import React, { useState, useRef } from 'react';
import { expenseApi, accountingApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Upload, Trash2, Loader2, CheckCircle2, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const SCHEDULE_C = [
  'Car & Truck', 'Legal & Professional', 'Materials & Supplies',
  'Contract Labor', 'Tools & Equipment Rentals', 'Other Business Expenses',
];
const JOB_CATEGORIES = [
  'materials', 'labor', 'subcontractor', 'equipment_rental',
  'permits', 'delivery', 'tools', 'disposal', 'other',
];
const today = () => new Date().toISOString().slice(0, 10);

let ROW_ID = 0;

export default function BulkReceiptImport({ projects = [], onDone }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef(null);

  const reset = () => setRows([]);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const newRows = list.map((file) => ({
      id: ++ROW_ID, file, status: 'scanning',
      description: '', vendor: '', amount: '', date: '',
      schedule_c_category: 'Other Business Expenses', category: 'other', project_id: '',
    }));
    setRows((prev) => [...prev, ...newRows]);
    // Scan sequentially to be gentle on the AI quota
    for (const row of newRows) {
      try {
        const res = await expenseApi.scanReceipt(row.file);
        const d = res.data || {};
        setRows((prev) => prev.map((r) => r.id === row.id ? {
          ...r, status: 'ready',
          description: d.description || d.vendor || '',
          vendor: d.vendor || '',
          amount: d.total ? String(d.total) : '',
          date: d.date || '',
          schedule_c_category: d.schedule_c_category || 'Other Business Expenses',
          category: d.category || 'other',
        } : r));
      } catch (err) {
        setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: 'error' } : r));
      }
    }
  };

  const setField = (id, field, value) => setRows((prev) => prev.map((r) => r.id === id ? { ...r, [field]: value } : r));
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));

  const createAll = async () => {
    const ready = rows.filter((r) => r.status === 'ready' && parseFloat(r.amount) > 0 && r.description.trim());
    if (!ready.length) { toast.error('No complete rows to create'); return; }
    setCreating(true);
    let ok = 0, fail = 0;
    for (const r of ready) {
      const payload = {
        description: r.description.trim(),
        amount: parseFloat(r.amount),
        vendor: r.vendor,
        date: r.date || today(),
        schedule_c_category: r.schedule_c_category,
        receipt: r.file,
      };
      try {
        if (r.project_id) {
          await expenseApi.create(r.project_id, { ...payload, category: r.category || 'materials' });
        } else {
          await accountingApi.addBusinessExpense(payload);
        }
        ok++;
        setRows((prev) => prev.filter((x) => x.id !== r.id));
      } catch (e) {
        fail++;
        setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, status: 'error' } : x));
      }
    }
    setCreating(false);
    if (ok) toast.success(`Created ${ok} expense${ok > 1 ? 's' : ''}${fail ? `, ${fail} failed` : ''}`);
    else if (fail) toast.error('Failed to create expenses');
    if (onDone) onDone();
    if (!rows.filter((r) => r.id).length) setOpen(false);
  };

  const readyCount = rows.filter((r) => r.status === 'ready').length;
  const scanning = rows.some((r) => r.status === 'scanning');

  return (
    <>
      <Button variant="outline" className="rounded-sm" onClick={() => { reset(); setOpen(true); }} data-testid="bulk-import-btn">
        <Upload className="h-4 w-4 mr-2" /> Bulk Import Receipts
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Import Receipts</DialogTitle>
            <DialogDescription>Drop several receipts — each is scanned by AI. Review, tweak, then create them all at once.</DialogDescription>
          </DialogHeader>

          <input ref={inputRef} type="file" multiple accept="image/*,application/pdf,.pdf,.heic,.heif" className="hidden"
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} data-testid="bulk-file-input" />
          <Button variant="outline" className="w-full border-dashed" onClick={() => inputRef.current?.click()} disabled={creating}>
            <Sparkles className="h-4 w-4 mr-2" /> Add Receipts (photos or PDFs)
          </Button>

          {rows.length > 0 && (
            <div className="space-y-2 mt-3">
              {rows.map((r) => (
                <div key={r.id} className="border rounded-lg p-3 space-y-2" data-testid={`bulk-row-${r.id}`}>
                  <div className="flex items-center gap-2 text-xs">
                    {r.status === 'scanning' && <><Loader2 className="h-4 w-4 animate-spin text-blue-600" /> <span className="text-blue-600">Reading {r.file.name}…</span></>}
                    {r.status === 'ready' && <><CheckCircle2 className="h-4 w-4 text-green-600" /> <span className="text-muted-foreground truncate">{r.file.name}</span></>}
                    {r.status === 'error' && <><AlertCircle className="h-4 w-4 text-red-600" /> <span className="text-red-600 truncate">Couldn't read {r.file.name} — enter manually</span></>}
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 ml-auto text-red-600" onClick={() => removeRow(r.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                  {r.status !== 'scanning' && (
                    <div className="grid grid-cols-12 gap-2">
                      <Input className="col-span-4 h-8 text-sm" placeholder="Description" value={r.description} onChange={(e) => setField(r.id, 'description', e.target.value)} data-testid={`bulk-desc-${r.id}`} />
                      <Input className="col-span-3 h-8 text-sm" placeholder="Vendor" value={r.vendor} onChange={(e) => setField(r.id, 'vendor', e.target.value)} />
                      <Input className="col-span-2 h-8 text-sm" type="number" step="0.01" placeholder="$" value={r.amount} onChange={(e) => setField(r.id, 'amount', e.target.value)} data-testid={`bulk-amount-${r.id}`} />
                      <Input className="col-span-3 h-8 text-sm" type="date" value={r.date} onChange={(e) => setField(r.id, 'date', e.target.value)} />
                      <div className="col-span-6">
                        <Select value={r.schedule_c_category} onValueChange={(v) => setField(r.id, 'schedule_c_category', v)}>
                          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>{SCHEDULE_C.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-6">
                        <Select value={r.project_id || 'none'} onValueChange={(v) => setField(r.id, 'project_id', v === 'none' ? '' : v)}>
                          <SelectTrigger className="h-8 text-sm" data-testid={`bulk-project-${r.id}`}><SelectValue placeholder="Overhead (no job)" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Overhead (no job)</SelectItem>
                            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {r.project_id && (
                        <div className="col-span-6">
                          <Select value={r.category || 'materials'} onValueChange={(v) => setField(r.id, 'category', v)}>
                            <SelectTrigger className="h-8 text-sm" data-testid={`bulk-jobcat-${r.id}`}><SelectValue placeholder="Job cost category" /></SelectTrigger>
                            <SelectContent>
                              {JOB_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            <Button onClick={createAll} disabled={creating || scanning || readyCount === 0} data-testid="bulk-create-all-btn">
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Create {readyCount || ''} Expense{readyCount === 1 ? '' : 's'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
